import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { reveal } from '@Guidelines/Theme/helpers/animations';
import { theme } from '@Guidelines/Theme/theme';
import PlainDate from '@Domain/PlainDate';
import type { OutboxItem } from '@Domain/Ports/Outbox/OutboxPort';
import formatDuration from '@UI/Helpers/Outbox/formatDuration';
import useLocalInterviews from '@UI/Pages/Beneficiaries/Hooks/useLocalInterviews';
import CAPTURE_TIMESLICE_MS from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/Shared/captureTimesliceMs';
import DiscardLocalDataDialog from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/ConfirmationDialogs/DiscardLocalDataDialog';
import useRecoveredChunkCount from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewUpload/InterruptedRecordingBanner/Hooks/useRecoveredChunkCount';
import useLocalInterviewPort from '@UI/Providers/PortsProvider/Hooks/LocalInterviews/useLocalInterviewPort';
import useOutboxPort from '@UI/Providers/PortsProvider/Hooks/OutboxRunner/useOutboxPort';
import { css } from '@emotion/react';
import { AlertTriangle, UploadCloud } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@verclam/ui-kit';

const bannerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1.25rem 1.5rem;
	border-radius: ${theme.radii.large};
	background-color: ${theme.states.warning.surface};
	color: ${theme.states.warning.ink};
	font-family: ${theme.fonts.body};
`;

const headerStyle = css`
	display: flex;
	align-items: center;
	gap: 0.75rem;
`;

const titleStyle = css`
	${theme.typography.cardTitle}
	margin: 0;
	color: inherit;
`;

const textStyle = css`
	margin: 0;
	font-size: ${theme.fontSizes.large};
	color: inherit;
`;

const durationStyle = css`
	${textStyle}
	font-family: ${theme.fonts.mono};
`;

const actionsStyle = css`
	display: flex;
	flex-wrap: wrap;
	gap: 0.75rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		flex-direction: column;
	}
`;

const PARTIAL_KEPT = "Une partie de l'enregistrement a été conservée sur cet appareil avant l'interruption.";
const SEND_OR_DISCARD = 'Voulez-vous envoyer ce qui a été enregistré, ou le supprimer ?';

type Props = {
	readonly subject: SubjectAddress;
	readonly outboxItem: OutboxItem;
};

/**
 * A recording stopped before it was finished: the user decides between sending what the device
 * kept and erasing it. Nothing is sent unless the user asks.
 */
const InterruptedRecordingBanner: React.FC<Props> = ({ subject, outboxItem }) => {
	const navigate = useNavigate();
	const outboxPort = useOutboxPort();
	const localInterviewPort = useLocalInterviewPort();
	const recovered = useRecoveredChunkCount(outboxItem.id);
	const localInterviewId = String(outboxItem.context.localInterviewId ?? '');
	const localInterview = useLocalInterviews().find((interview) => interview.id === localInterviewId);
	const isImported = localInterview?.source === 'imported';
	const conductedOnDisplay = PlainDate.fromApi(localInterview?.conductedOn ?? '')?.toDisplay() ?? '';

	const [isDiscardDialogVisible, setIsDiscardDialogVisible] = React.useState(false);
	const [isSending, setIsSending] = React.useState(false);
	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const text = React.useMemo(() => {
		if (isImported) {
			return "L'import de ce fichier n'a pas été terminé. Supprimez-le de cet appareil, puis importez à nouveau le fichier.";
		}
		if (recovered.type === 'loading') {
			return 'Vérification de ce qui a été conservé sur cet appareil…';
		}
		if (recovered.type === 'read' && recovered.chunkCount === 0) {
			return "Aucun son n'a pu être conservé avant l'interruption. Vous pouvez supprimer cet enregistrement de cet appareil.";
		}
		if (recovered.type === 'read') {
			return PARTIAL_KEPT;
		}
		return `${PARTIAL_KEPT} ${SEND_OR_DISCARD}`;
	}, [isImported, recovered]);

	const recoveredDuration = React.useMemo(
		() =>
			!isImported && recovered.type === 'read' && recovered.chunkCount > 0
				? `Durée récupérée : environ ${formatDuration(recovered.chunkCount * CAPTURE_TIMESLICE_MS)}`
				: null,
		[isImported, recovered],
	);

	const canSend = !isImported && !(recovered.type === 'read' && recovered.chunkCount === 0);

	const handleSend = React.useCallback(() => {
		setErrorMessage(null);
		setIsSending(true);
		outboxPort
			.markReady(outboxItem.id, { durationMs: null })
			.catch((reason: unknown) => {
				setErrorMessage(reason instanceof Error ? reason.message : "L'envoi n'a pas pu être lancé.");
			})
			.finally(() => {
				setIsSending(false);
			});
	}, [outboxPort, outboxItem.id]);

	const handleDiscardAccept = React.useCallback(() => {
		setErrorMessage(null);
		setIsDiscardDialogVisible(false);
		outboxPort
			.discard(outboxItem.id)
			.then(() => localInterviewPort.remove(localInterviewId))
			.then(() => {
				navigate(buildSubjectPath(subject), { replace: true });
			})
			.catch((reason: unknown) => {
				setErrorMessage(reason instanceof Error ? reason.message : 'La suppression a échoué.');
			});
	}, [outboxPort, localInterviewPort, localInterviewId, outboxItem.id, subject, navigate]);

	const handleOpenDiscardDialog = React.useCallback(() => setIsDiscardDialogVisible(true), []);
	const handleHideDiscardDialog = React.useCallback(() => setIsDiscardDialogVisible(false), []);

	return (
		<section css={[bannerStyle, reveal(0)]} role="alert">
			<div css={headerStyle}>
				<AlertTriangle size={22} aria-hidden="true" />
				<h2 css={titleStyle}>Cet enregistrement a été interrompu.</h2>
			</div>
			<p css={textStyle}>{text}</p>
			<ConditionalLoader data={recoveredDuration}>
				{(duration) => (
					<React.Fragment>
						<p css={durationStyle}>{duration}</p>
						<p css={textStyle}>{SEND_OR_DISCARD}</p>
					</React.Fragment>
				)}
			</ConditionalLoader>
			<ConditionalLoader data={errorMessage}>
				{(message) => <p css={textStyle}>{message}</p>}
			</ConditionalLoader>
			<div css={actionsStyle}>
				<ConditionalLoader data={canSend}>
					<Button
						type="button"
						label="Envoyer l'enregistrement"
						icon={<UploadCloud size={16} />}
						loading={isSending}
						disabled={recovered.type === 'loading'}
						onClick={handleSend}
					/>
				</ConditionalLoader>
				<Button
					type="button"
					severity="danger"
					label="Supprimer l'enregistrement"
					onClick={handleOpenDiscardDialog}
				/>
			</div>
			<DiscardLocalDataDialog
				visible={isDiscardDialogVisible}
				conductedOnDisplay={conductedOnDisplay}
				onHide={handleHideDiscardDialog}
				onAccept={handleDiscardAccept}
			/>
		</section>
	);
};

export default InterruptedRecordingBanner;
